import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Download, Trash2, Upload } from "lucide-react";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const EmployeeDocsPage = () => {
  const { empNo } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef();

  // Build required auth headers for backend
  const buildHeaders = () => ({
    'user-id': localStorage.getItem('token'),
    'user-type': localStorage.getItem('userType'),
    'company-id': localStorage.getItem('companyId'),
  });

  useEffect(() => {
    fetchEmployee();
    fetchFiles();
    // eslint-disable-next-line
  }, [empNo]);

  const fetchEmployee = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`, {
        headers: buildHeaders(),
      });
      const found = response.data.find((emp) => emp.empNo === empNo);
      setEmployee(found);
    } catch (err) {
      setError("Could not fetch employee details");
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/empdocs/${empNo}/list`, {
        headers: buildHeaders(),
      });
      setFiles(response.data);
    } catch (err) {
      setError("Could not fetch files");
    }
  };

  const handleUpload = async (e) => {
    const filesToUpload = e.target.files;
    if (!filesToUpload || filesToUpload.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      for (let file of filesToUpload) {
        formData.append("docs", file);
      }
      await axios.post(`${API_URL}/empdocs/${empNo}/upload`, formData, {
        headers: { ...buildHeaders(), "Content-Type": "multipart/form-data" },
      });
      fetchFiles();
    } catch (err) {
      setError("Upload failed");
    }
    setUploading(false);
  };

  const handleDelete = async (filename) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(
        `${API_URL}/empdocs/${empNo}/delete/${encodeURIComponent(filename)}`,
        { headers: buildHeaders() }
      );
      fetchFiles();
    } catch (err) {
      setError("Delete failed");
    }
  };

  const handleDownload = (filename) => {
    // Use axios with headers and download as blob
    axios
      .get(`${API_URL}/empdocs/${empNo}/download/${encodeURIComponent(filename)}`, {
        headers: buildHeaders(),
        responseType: 'blob',
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setError('Download failed'));
  };

  const handleDownloadZip = () => {
    axios
      .get(`${API_URL}/empdocs/${empNo}/download-all`, {
        headers: buildHeaders(),
        responseType: 'blob',
      })
      .then((response) => {
        const disposition = response.headers['content-disposition'] || '';
        const match = disposition.match(/filename="?([^";]+)"?/i);
        const filename = match ? match[1] : `${empNo}-documents.zip`;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setError('Download failed'));
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-900 to-blue-700 py-10 px-2">
      <div className="max-w-4xl mx-auto rounded-2xl shadow-xl bg-white/80 backdrop-blur-md">
        {/* Header Bar */}
        <div className="rounded-t-2xl bg-gradient-to-r from-blue-700 to-indigo-700 px-8 py-5 flex flex-col sm:flex-row items-center justify-between">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-white hover:underline text-sm mb-2 sm:mb-0"
            >
              ← Back to Employee List
            </button>
            <h2 className="text-2xl font-bold text-white">Employee Documents</h2>
            {employee && (
              <div className="mt-1 text-blue-100 text-lg font-medium">
                <b>{employee.name}</b> ({employee.empNo})
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-4 sm:mt-0">
            <button
              onClick={handleDownloadZip}
              className="bg-blue-600 hover:bg-blue-800 text-white px-4 py-2 rounded-lg font-semibold flex items-center shadow"
            >
              <Download className="mr-2" size={18} /> Download All as Zip
            </button>
            <label className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center cursor-pointer shadow">
              <Upload className="mr-2" size={18} /> Upload Files
              <input
                type="file"
                multiple
                className="hidden"
                ref={fileInputRef}
                onChange={handleUpload}
                disabled={uploading}
              />
            </label>
          </div>
        </div>

        {/* Error Message */}
        {error && <div className="bg-red-100 text-red-700 px-4 py-2 rounded-b-xl font-medium">{error}</div>}

        {/* File List Card */}
        <div className="p-8">
          <div className="bg-white rounded-xl shadow border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-700">Uploaded Documents</h3>
              <span className="text-sm text-gray-500">{files.length} file{files.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-max whitespace-nowrap">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-3 text-left">File Name</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="p-4 text-center text-gray-400">
                        No documents found.
                      </td>
                    </tr>
                  ) : (
                    files.map((filename) => (
                      <tr key={filename} className="border-t hover:bg-blue-50/40 transition">
                        <td className="p-3 font-mono text-blue-900">{filename}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleDownload(filename)}
                            className="bg-blue-500 hover:bg-blue-700 text-white px-3 py-1 rounded-lg font-semibold flex items-center shadow"
                          >
                            <Download size={16} className="mr-1" /> Download
                          </button>
                          <button
                            onClick={() => handleDelete(filename)}
                            className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded-lg font-semibold flex items-center shadow"
                          >
                            <Trash2 size={16} className="mr-1" /> Delete
                          </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDocsPage;
